import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import StickyBottomNav from "../../../components/Navbar/StickyBottomNav";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { BookOpen, User, Mail, Phone, School, Users, FileText, AlertCircle, Check } from 'lucide-react';

const BranchCreation = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    institution: "",
    motivation: "",
    refugeeIssues: "",
    leadership: "",
    membershipInterest: "",
    facultySupport: "",
    activities: "",
    communityEngagement: "",
    localCollaborations: "",
    funding: "",
    supportNeeded: "",
    commitment: false
  });

  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const GOOGLE_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSevIkFwoHr7UUzLdUKXvj78r9JJ5WV88_rp8-IQmvgg9p-u-A/formResponse";

  const GOOGLE_FORM_FIELDS = {
    name: "entry.1799182697",
    email: "entry.1547985899",
    phoneNumber: "entry.1166557629",
    institution: "entry.581641062",
    motivation: "entry.1526520824",
    refugeeIssues: "entry.109956558",
    leadership: "entry.1885662826",
    membershipInterest: "entry.392249357",
    facultySupport: "entry.1625851622",
    activities: "entry.585993317",
    communityEngagement: "entry.1485805692",
    localCollaborations: "entry.1382103954",
    funding: "entry.974061373",
    supportNeeded: "entry.2118416777",
    commitment: "entry.987179961"
  };

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ""
          : "Please enter a valid email address";
      case "phoneNumber":
        if (!value) {
          return "Phone number is required";
        }
        if (!isValidPhoneNumber(value)) {
          return "Please enter a valid phone number for the selected country";
        }
        return "";
      case "commitment":
        return value ? "" : "You must agree to the commitment statement";
      default:
        return value.toString().trim() ? "" : "This field is required";
    }
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, type === "checkbox" ? checked : value);
    setValidationErrors((prev) => ({
      ...prev,
      [name]: error
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormState((prev) => ({
      ...prev,
      [name]: newValue
    }));

    if (touched[name]) {
      const error = validateField(name, newValue);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    // Validate all fields
    const errors = {};
    Object.entries(formState).forEach(([name, value]) => {
      const error = validateField(name, value);
      if (error) errors[name] = error;
    });

    setValidationErrors(errors);
    setTouched(
      Object.keys(formState).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      setShowErrorMessage(true);
      const firstError = document.querySelector('[aria-invalid="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      // Create form data object with Google Form field IDs
      const formData = new FormData();
      Object.entries(formState).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === "commitment") {
            formData.append(GOOGLE_FORM_FIELDS[key], value ? "Yes" : "");
          } else {
            formData.append(GOOGLE_FORM_FIELDS[key], value.toString());
          }
        }
      });

      await fetch(GOOGLE_FORM_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors" // Required for submitting to Google Forms
      });

      // Since we're using no-cors, we won't get a proper response status
      // We'll assume success if no error is thrown
      setShowSuccessMessage(true);
      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Submission error:", error);
      setShowErrorMessage(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000); // Hide after 10 seconds
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  useEffect(() => {
    if (showErrorMessage) {
      const timer = setTimeout(() => {
        setShowErrorMessage(false);
      }, 10000); // Hide after 10 seconds
      return () => clearTimeout(timer);
    }
  }, [showErrorMessage]);

  const resetForm = () => {
    setFormState({
      name: "",
      email: "",
      phoneNumber: "",
      institution: "",
      motivation: "",
      refugeeIssues: "",
      leadership: "",
      membershipInterest: "",
      facultySupport: "",
      activities: "",
      communityEngagement: "",
      localCollaborations: "",
      funding: "",
      supportNeeded: "",
      commitment: false
    });
    setTouched({});
    setValidationErrors({});
  };

  const inputClassName = (name) => `
    w-full px-4 py-3 rounded-lg transition-all duration-200
    ${
      touched[name] && validationErrors[name]
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-lightTeal focus:ring-2 focus:ring-primary focus:border-primary"
    }
    ${isSubmitting ? "bg-offWhite" : "bg-white"}
  `;

  const phoneInputClassName = `
    w-full px-4 py-3 rounded-lg transition-all duration-200
    ${
      touched.phoneNumber && validationErrors.phoneNumber
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-lightTeal focus:ring-2 focus:ring-primary focus:border-primary"
    }
    ${isSubmitting ? "bg-offWhite" : "bg-white"}
  `;

  // Custom styles for phone input
  const phoneInputCustomStyles = `
    .PhoneInputCountry {
      margin-right: 0.75rem;
    }
    .PhoneInputCountrySelect {
      padding: 0.5rem;
      border-radius: 0.5rem;
      border-color: #E8F3F4;
    }
    .PhoneInputInput {
      font-family: 'Lato', sans-serif;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border-color: #E8F3F4;
    }
    .PhoneInputInput:focus {
      border-color: #53C2C8;
      outline: none;
      box-shadow: 0 0 0 2px rgba(83, 194, 200, 0.2);
    }
  `;

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">
              Start a New Chapter
            </h1>
            <p className="text-bodyRegular text-textPrimary max-w-3xl mx-auto mb-6">
              Thank you for your interest in starting a Students For Refugees
              chapter! Please complete this form to begin the process. We will
              review your application and contact you with next steps within 5-10 days.
            </p>
            <div className="w-16 h-1 bg-cta mx-auto mb-6"></div>
            
            <div className="bg-white p-6 rounded-xl shadow-md inline-flex items-center">
              <BookOpen size={24} className="text-primary mr-3 flex-shrink-0" />
              <div className="text-left">
                <p className="text-textPrimary mb-2">Please refer to our chapter charter when responding to the form:</p>
                <a
                  href="https://docs.google.com/document/d/1csPmPiM1iFq4hsZwlyW1sNezZ5idgXOCqUwF6WJeADk/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-bold hover:underline flex items-center"
                >
                  <span>Chapter Charter Document</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Notifications */}
            {showSuccessMessage && (
              <div className="bg-primary/10 border-l-4 border-primary p-4 mb-6" role="alert">
                <div className="flex items-start">
                  <Check size={24} className="text-primary mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-display text-lg text-primary mb-1">Application Submitted</h4>
                    <p className="text-textPrimary">
                      Your chapter application has been successfully submitted! You
                      will be contacted within 5-10 days regarding next steps.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {showErrorMessage && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" role="alert">
                <div className="flex items-start">
                  <AlertCircle size={24} className="text-red-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-display text-lg text-red-700 mb-1">Submission Error</h4>
                    <p className="text-red-600">
                      There was an error submitting your application. Please try again
                      later or contact us directly if the problem persists.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6 md:p-8" noValidate>
              {/* Contact Information */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <User size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Contact Information</h2>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Full Name <span className="text-cta">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={inputClassName("name")}
                      required
                      disabled={isSubmitting}
                      aria-invalid={touched.name && !!validationErrors.name}
                    />
                    {touched.name && validationErrors.name && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Email Address <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputClassName("email")} pl-10`}
                        required
                        disabled={isSubmitting}
                        aria-invalid={touched.email && !!validationErrors.email}
                        placeholder="example@email.com"
                      />
                    </div>
                    {touched.email && validationErrors.email && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Phone Number <span className="text-cta">*</span>
                    </label>

                    <div className="relative">
                      <PhoneInput
                        international
                        defaultCountry="US"
                        countrySelectProps={{
                          "aria-label": "Select country",
                          className: "absolute left-0 top-0 bottom-0"
                        }}
                        value={formState.phoneNumber}
                        onChange={(value) => {
                          setFormState((prev) => ({
                            ...prev,
                            phoneNumber: value
                          }));

                          if (touched.phoneNumber) {
                            const error = validateField("phoneNumber", value);
                            setValidationErrors((prev) => ({
                              ...prev,
                              phoneNumber: error
                            }));
                          }
                        }}
                        onBlur={() => {
                          setTouched((prev) => ({
                            ...prev,
                            phoneNumber: true
                          }));

                          const error = validateField(
                            "phoneNumber",
                            formState.phoneNumber
                          );
                          setValidationErrors((prev) => ({
                            ...prev,
                            phoneNumber: error
                          }));
                        }}
                        className={phoneInputClassName}
                        disabled={isSubmitting}
                      />
                    </div>

                    {touched.phoneNumber && validationErrors.phoneNumber && (
                      <p className="mt-1 text-sm text-red-500" role="alert">
                        {validationErrors.phoneNumber}
                      </p>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      Select country and enter your full phone number
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Institution Type <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <School size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        name="institution"
                        value={formState.institution}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputClassName("institution")} pl-10 appearance-none`}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Select your institution type</option>
                        <option value="Middle School">Middle School</option>
                        <option value="High School">High School</option>
                        <option value="University">University</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                    {touched.institution && validationErrors.institution && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.institution}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              {/* Chapter Details */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <Users size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Chapter Details</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      What motivates you to start a Students for Refugees chapter?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="motivation"
                      value={formState.motivation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("motivation")}
                      required
                      disabled={isSubmitting}
                      placeholder="Share your inspiration and goals..."
                    />
                    {touched.motivation && validationErrors.motivation && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.motivation}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Are there specific refugee issues you are particularly
                      interested in supporting?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="refugeeIssues"
                      value={formState.refugeeIssues}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      className={inputClassName("refugeeIssues")}
                      required
                      disabled={isSubmitting}
                      placeholder="Education, healthcare, integration, etc..."
                    />
                    {touched.refugeeIssues && validationErrors.refugeeIssues && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.refugeeIssues}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Who will lead the chapter? Include titles and names{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="leadership"
                      value={formState.leadership}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Example: President - Jane Doe, Vice President - John Smith"
                      className={inputClassName("leadership")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.leadership && validationErrors.leadership && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.leadership}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Do you have membership interest at your institution?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="membershipInterest"
                      value={formState.membershipInterest}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Detail current interest or plans to increase membership"
                      className={inputClassName("membershipInterest")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.membershipInterest &&
                      validationErrors.membershipInterest && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.membershipInterest}
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Do you have an established sponsor/faculty support?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="facultySupport"
                        value={formState.facultySupport}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputClassName("facultySupport")} appearance-none`}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Select an option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                    {touched.facultySupport &&
                      validationErrors.facultySupport && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.facultySupport}
                        </p>
                      )}
                  </div>
                </div>
              </section>

              {/* Planning & Implementation */}
              <section className="mb-10">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-3">
                    <FileText size={20} className="text-white" />
                  </div>
                  <h2 className="font-display text-h3 text-secondary">Planning & Implementation</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      What unique activities, programs, or events do you envision?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="activities"
                      value={formState.activities}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Describe your planned activities and initiatives"
                      className={inputClassName("activities")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.activities && validationErrors.activities && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.activities}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      How do you plan to engage students, faculty, and the broader
                      community? <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="communityEngagement"
                      value={formState.communityEngagement}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Outline your engagement strategies"
                      className={inputClassName("communityEngagement")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.communityEngagement &&
                      validationErrors.communityEngagement && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.communityEngagement}
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Are there any local refugee communities or organizations
                      that you would aim to collaborate with?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="localCollaborations"
                      value={formState.localCollaborations}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="List potential community partners"
                      className={inputClassName("localCollaborations")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.localCollaborations &&
                      validationErrors.localCollaborations && (
                        <p className="mt-1 text-sm text-red-500">
                          {validationErrors.localCollaborations}
                        </p>
                      )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      How do you plan to fund chapter activities?{" "}
                      <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="funding"
                      value={formState.funding}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Detail your funding plans (fundraisers, school grants, donations, etc.). Note: $80 annual chapter dues required."
                      className={inputClassName("funding")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.funding && validationErrors.funding && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.funding}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      What support or resources do you need from the national
                      team? <span className="text-cta">*</span>
                    </label>
                    <textarea
                      name="supportNeeded"
                      value={formState.supportNeeded}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={4}
                      placeholder="Describe what would help you succeed"
                      className={inputClassName("supportNeeded")}
                      required
                      disabled={isSubmitting}
                    />
                    {touched.supportNeeded && validationErrors.supportNeeded && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.supportNeeded}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              {/* Commitment Statement */}
              <div className="mb-10">
                <div className="flex items-start p-4 bg-lightTeal rounded-lg">
                  <div className="flex items-center h-5 mt-1">
                    <input
                      id="commitment"
                      name="commitment"
                      type="checkbox"
                      checked={formState.commitment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary
                        ${
                          touched.commitment && validationErrors.commitment
                            ? "border-red-500"
                            : ""
                        }`}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="ml-3">
                    <label
                      htmlFor="commitment"
                      className="text-sm text-textPrimary font-medium"
                    >
                      I understand that starting a Students for Refugees chapter
                      is a commitment to uphold the organization's mission and
                      to engage in meaningful, respectful advocacy for refugees.{" "}
                      <span className="text-cta">*</span>
                    </label>
                    {touched.commitment && validationErrors.commitment && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationErrors.commitment}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-cta text-white py-4 px-8 rounded-xl text-button font-body transition-all duration-300 shadow-md 
                  ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg"}`}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                {isSubmitting ? "Submitting..." : "Submit Chapter Application"}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
      <StickyBottomNav />
      
      {/* Custom phone input styles */}
      <style jsx global>{phoneInputCustomStyles}</style>
    </div>
  );
};

export default BranchCreation;